import { Input, Typography } from '@smartgift/frontend-toolkit';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WithoutContactRecipientVerification } from '@hero/components/organisms/Recognition';
import { RECOGNITION_CONTACTS_TAB } from '@hero/constants';
import { CONTACT_LIMIT_NUMBER, DEFAULT_CONTACT_LIMIT_NUMBER } from '@hero/constants/merchant';
import { useDebounceEffect } from '@hero/hooks';
import { OnboardingCard, ShareAnywhere } from '@hero/molecules';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useGetRecognitionRecipientList } from '@hero/services/order-service';
import { useOnboardingStore } from '@hero/store/hooks';

import * as S from './style';

export default function ContactIndividualContent({
  updateContact,
  recognitionCode,
  recipientQuantity,
  recipientVerification
}) {
  const { t } = useTranslation();
  const { recognition: onboardingRecognition } = useOnboardingStore();
  const [maxCount, setMaxCount] = useState(recipientQuantity || '');

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: recognitionCode
    },
    {
      enabled: !!recognitionCode
    }
  );
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const [isOnboardingCardShown, setIsOnboardingCardShown] = useState(true);

  const contactNumberLimit =
    merchantData?.configs[CONTACT_LIMIT_NUMBER] ?? DEFAULT_CONTACT_LIMIT_NUMBER;

  const totalRecipients = useMemo(() => {
    if (recognitionCode) {
      return recognitionRecipients?.totalDocs;
    }
  }, [recognitionRecipients, recognitionCode]);

  const maxContactHasError = useMemo(() => {
    return recipientQuantity >= contactNumberLimit;
  }, [merchantData, recipientVerification, totalRecipients, recipientQuantity]);

  useDebounceEffect(
    async () => {
      if (maxCount && maxCount !== (recipientQuantity || '')) {
        updateContact({
          contacts: {
            maxCount: Number(maxCount),
            type: RECOGNITION_CONTACTS_TAB.number,
            ids: []
          }
        });
      }
    },
    1000,
    [maxCount]
  );

  return (
    <S.ContactIndividualContentWrapper>
      <S.QuantityContainer>
        <OnboardingCard
          styles={{
            wrapper: {
              right: '8vw',
              top: '7vh'
            }
          }}
          cardOffset={[12, 10]}
          position="right-start"
          isInfoActive={onboardingRecognition?.infoCardsActive}
          featureType="recognition"
          isCardShown={isOnboardingCardShown}
          setIsCardShown={setIsOnboardingCardShown}
          title={t('enterRecipientQuantity')}
          description={t('enterRecipientQuantityDesc')}
        />
        <S.RecipientQuantityInputArea>
          <Typography style={{ marginBottom: 16 }} color="fg" variant="body1Bold">
            {t('enterTotalRecipients')}:
          </Typography>

          <Input
            variant="outlined"
            error={maxContactHasError}
            value={maxCount}
            size="lg"
            name="recipientQuantity"
            label={`${t('recipientQuantity')}*`}
            min={1}
            maxLength={5}
            max={100000}
            onChange={event => {
              setMaxCount(Number(event.target.value) || '');
            }}
            style={{ width: 372 }}
          />

          {maxContactHasError && (
            <S.MaxContactError>
              {t(
                recipientQuantity >= contactNumberLimit
                  ? 'recipientLimitTitle'
                  : 'recipientEmailLimitTitle'
              )}
            </S.MaxContactError>
          )}
        </S.RecipientQuantityInputArea>
        <WithoutContactRecipientVerification
          recipientVerification={recipientVerification}
          updateRecipientVerification={type => {
            updateContact({ settings: { recipientVerification: { ...type } } });
          }}
          sections={{ hasEmailList: false }}
          //? if hasEmailList false, then you don't need to pass recognitionCode, recognitionMaxCount, recipientQuantityRef
        />
      </S.QuantityContainer>
      <ShareAnywhere />
    </S.ContactIndividualContentWrapper>
  );
}
